import React from 'react';
import { DatePicker } from '@roger/ui-components';
import {
  formatDateToISO,
  formatDateToYYYYMMDD,
  parseISOToLocalDate,
  parseYYYYMMDD,
} from '../../../../utils/dateUtils';
import { useBespokeSectionContext } from './BespokeSectionContext';
import { useQuestionFieldDisabled } from './useQuestionFieldDisabled';
import { QidTag } from '../../../dev/QidTag';
import {
  joinClasses,
  BESPOKE_FIELD_LABEL_CLASS,
  BESPOKE_FIELD_STACK_GAP_CLASS,
} from './fieldClassSlots';
import { resolveControlClassName } from './resolveFieldClassNames';
import { CmsFieldError } from '../../CmsFieldError';
import { useCmsQuestionError } from '../../cmsFieldErrors';

export interface OASISDateRenderProps {
  /** YYYY-MM-DD string */
  value: string;
  /** Call with a YYYY-MM-DD string; stored internally as YYYYMMDD */
  onChange: (v: string) => void;
  label: string;
}

export interface OASISDateProps {
  qid: string;
  /** Display-only replacement for the schema prompt; answer binding still uses qid. */
  labelOverride?: string;
  className?: string;
  style?: React.CSSProperties;
  hideLabel?: boolean;
  innerClassName?: string;
  labelClassName?: string;
  controlClassName?: string;
  /** @deprecated Prefer `controlClassName`. */
  inputClassName?: string;
  children?: (props: OASISDateRenderProps) => React.ReactNode;
}

export function OASISDate({
  qid,
  labelOverride,
  className,
  style,
  hideLabel,
  innerClassName,
  labelClassName,
  controlClassName,
  inputClassName,
  children,
}: OASISDateProps) {
  const { answers, onAnswerChange, schemaMap } = useBespokeSectionContext();
  const cmsError = useCmsQuestionError(qid);
  const question = schemaMap.get(qid);
  const inheritedDisabled = useQuestionFieldDisabled(question);

  const rawAnswer = answers[qid];
  const dateValue =
    rawAnswer instanceof Date
      ? rawAnswer
      : rawAnswer
        ? (parseYYYYMMDD(String(rawAnswer)) ??
          parseISOToLocalDate(String(rawAnswer)))
        : undefined;
  const value = dateValue ? formatDateToISO(dateValue) : '';
  const onChange = (v: string) => onAnswerChange(qid, v.replace(/-/g, ''));
  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      onAnswerChange(qid, formatDateToYYYYMMDD(date));
    } else {
      onAnswerChange(qid, '');
    }
  };
  const label =
    labelOverride ?? question?.question ?? question?.description ?? qid;
  const resolvedControlClassName = resolveControlClassName(
    controlClassName,
    inputClassName
  );

  return (
    <div
      data-qid={qid}
      className={joinClasses('min-w-0', className)}
      style={style}
    >
      <QidTag qid={qid} />
      {children ? (
        children({ value, onChange, label })
      ) : (
        <div className={innerClassName ?? BESPOKE_FIELD_STACK_GAP_CLASS}>
          {!hideLabel && (
            <label
              className={joinClasses(BESPOKE_FIELD_LABEL_CLASS, labelClassName)}
            >
              {label}
            </label>
          )}
          <DatePicker
            date={dateValue}
            onDateChange={handleDateChange}
            disabled={inheritedDisabled}
            placeholder='Select date...'
            datePickerSize='default'
            className={joinClasses('w-full', resolvedControlClassName)}
          />
        </div>
      )}
      <CmsFieldError fieldId={`oasis-q-${qid}`} message={cmsError} />
    </div>
  );
}
